import { useTranslation } from 'next-i18next';
import '../consts/events.quiz-classroom-link-nudge';
import { trackEvent } from '@zep/logger/tracking';
import type { ClassroomLinkNudgeCloseReason, ClassroomLinkNudgeContext } from '../consts/events.quiz-classroom-link-nudge';
import { type ContentShareType } from '../types';
import { ClassLinkNudgeView, type ClassLinkNudgeViewProps } from './ClassLinkNudgeView';
import { showClassroomLinkModal } from './ClassroomLinkModal';

/** 연결할 콘텐츠. id 와 type 은 늘 같이 다녀서 하나로 묶는다. */
export interface LinkTarget {
  id: string;
  type: ContentShareType;
}

/** 권유 창이 뜬 순간 1회. 띄우는 함수(`show*LinkNudge`)가 부른다. */
export const trackClassLinkNudgeView = (tracking: ClassroomLinkNudgeContext) => trackEvent('quiz_classroom_link_nudge_view', tracking);

/**
 * 권유 모달의 공통 동작. 버튼 클릭을 계측하고 모달을 닫은 뒤,
 * 「클래스 연결하기」면 연결 창을 연다. 화면은 `ClassLinkNudgeView` 가 그린다.
 * 모달마다 다른 것(그림·문구·보조 버튼 동작)만 props 로 받는다.
 */
export function ClassLinkNudge({
  tracking,
  content,
  secondaryCloseReason,
  onSecondary,
  onClose,
  ...view
}: {
  /** 계측 문맥. `place` 는 연결 창의 `source` 로도 넘어가 권유 → 연결 창 → 저장이 한 값으로 이어진다. */
  tracking: ClassroomLinkNudgeContext;
  content: LinkTarget;
  /** 보조 버튼이 무엇인지(다음에 할래요 / 하루 동안 보지 않기). */
  secondaryCloseReason: Exclude<ClassroomLinkNudgeCloseReason, 'dismiss'>;
  /** 보조 버튼을 눌렀을 때 닫기 전에 할 일 (예: 하루 동안 보지 않기). */
  onSecondary?: () => void;
  onClose: () => void;
} & Pick<ClassLinkNudgeViewProps, 'illustration' | 'title' | 'description' | 'secondaryLabel'>) {
  const {
    t
  } = useTranslation(['common']);
  const trackClose = (closeReason: ClassroomLinkNudgeCloseReason) => trackEvent('quiz_classroom_link_nudge_close', {
    ...tracking,
    close_reason: closeReason
  });
  return <ClassLinkNudgeView {...view} primaryLabel={t('common:classroomLink.nudgeLinkButton')} onPrimary={() => {
    trackEvent('quiz_classroom_link_nudge_click', tracking);
    onClose();
    showClassroomLinkModal({
      contentId: content.id,
      contentType: content.type,
      source: tracking.place
    });
  }} onSecondary={() => {
    trackClose(secondaryCloseReason);
    onSecondary?.();
    onClose();
  }} onClose={() => {
    // ✕·바깥 누름·끌어내림·ESC 는 모두 이 onClose 로 온다(구분하지 않는 `dismiss`).
    trackClose('dismiss');
    onClose();
  }} data-sentry-element="ClassLinkNudgeView" data-sentry-component="ClassLinkNudge" data-sentry-source-file="ClassLinkNudge.tsx" />;
}